"use client";

import Link from "next/link";
import * as React from "react";

import { captureCtaClick, type FunnelEvent, type FunnelSource } from "@/lib/analytics/events";

/* ──────────────────────────────────────────────────────────────────────────
 * TrackedLink — a drop-in next/link that fires a funnel CTA event on click.
 *
 * Use in place of <Link> on intentional conversion CTAs. Safe as the child of
 * <Button asChild> — Radix Slot.Root forwards className + ref, and any onClick
 * passed down is composed (we fire the event first, then call it).
 *
 * Fire-and-navigate: PostHog batches and sends with sendBeacon/keepalive, so
 * the event survives the navigation without us needing to delay the click.
 * ────────────────────────────────────────────────────────────────────────── */

type TrackedLinkProps = React.ComponentProps<typeof Link> & {
  /** Funnel event to fire on click. */
  event: FunnelEvent;
  /** Which surface this CTA lives on. */
  source: FunnelSource;
};

export function TrackedLink({ event, source, onClick, ...props }: TrackedLinkProps) {
  return (
    <Link
      {...props}
      onClick={(e) => {
        captureCtaClick(event, source);
        onClick?.(e);
      }}
    />
  );
}
